import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { OutcomePayload, OutcomeTrackingConfig, SessionConfig } from '../types';
import { getSessionId } from '../utils/session';

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function parseMaybeJson(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (!(trimmed.startsWith('{') || trimmed.startsWith('['))) return trimmed;
  try {
    return JSON.parse(trimmed);
  } catch {
    return trimmed;
  }
}

function normalizeOutcome(input: unknown): OutcomePayload | null {
  const parsed = parseMaybeJson(input);
  if (typeof parsed === 'string') {
    const name = parsed.trim();
    if (!name) return null;
    return { name };
  }

  if (!isPlainObject(parsed)) return null;

  const root = parsed as Record<string, unknown>;
  const candidate =
    isPlainObject(root.outcome) && !('name' in root)
      ? (root.outcome as Record<string, unknown>)
      : root;

  const nameValue =
    (typeof candidate.name === 'string' && candidate.name) ||
    (typeof candidate.outcome === 'string' && candidate.outcome) ||
    (typeof candidate.event === 'string' && candidate.event) ||
    '';
  const name = String(nameValue || '').trim();
  if (!name) return null;

  const provider = typeof candidate.provider === 'string' ? candidate.provider : undefined;
  const category = typeof candidate.category === 'string' ? candidate.category : undefined;
  const status = typeof candidate.status === 'string' ? candidate.status : undefined;
  const id = typeof candidate.id === 'string' ? candidate.id : undefined;
  const resourceType =
    typeof candidate.resourceType === 'string' ? candidate.resourceType : undefined;
  const resourceId = typeof candidate.resourceId === 'string' ? candidate.resourceId : undefined;
  const quantity = typeof candidate.quantity === 'number' ? candidate.quantity : undefined;
  const amount = typeof candidate.amount === 'number' ? candidate.amount : undefined;
  const currency = typeof candidate.currency === 'string' ? candidate.currency : undefined;

  const data = isPlainObject(candidate.data)
    ? (candidate.data as Record<string, unknown>)
    : undefined;

  // Preserve additional properties as `data` if no explicit `data` is provided.
  if (!data) {
    const extras: Record<string, unknown> = {};
    Object.entries(candidate).forEach(([key, value]) => {
      if (
        key === 'type' ||
        key === 'name' ||
        key === 'outcome' ||
        key === 'event' ||
        key === 'provider' ||
        key === 'category' ||
        key === 'status' ||
        key === 'id' ||
        key === 'resourceType' ||
        key === 'resourceId' ||
        key === 'quantity' ||
        key === 'amount' ||
        key === 'currency' ||
        key === 'data' ||
        key === 'payload'
      ) {
        return;
      }
      extras[key] = value;
    });
    if (Object.keys(extras).length > 0) {
      return {
        name,
        provider,
        category,
        status,
        id,
        resourceType,
        resourceId,
        quantity,
        amount,
        currency,
        data: extras,
      };
    }
  }

  return {
    name,
    provider,
    category,
    status,
    id,
    resourceType,
    resourceId,
    quantity,
    amount,
    currency,
    data,
  };
}

function originAllowed(origin: string, allowed?: string[]) {
  if (!origin) return false;
  if (!allowed || allowed.length === 0) return origin === window.location.origin;
  if (allowed.includes('*')) return true;
  return allowed.includes(origin);
}

interface UseOutcomeTrackingOptions {
  config?: OutcomeTrackingConfig | null;
  trackAnalytics: (action: string, data: Record<string, unknown>) => void;
  threadId: string | null;
  onOutcome?: (outcome: OutcomePayload & { threadId?: string; sessionId?: string | null }) => void;
  sessionConfig?: SessionConfig;
}

export function useOutcomeTracking({
  config,
  trackAnalytics,
  threadId,
  onOutcome,
  sessionConfig,
}: UseOutcomeTrackingOptions) {
  const listenEnabled = config?.enabled === true;
  const emitEnabled = config?.enabled !== false;

  const action = config?.action || 'outcome';
  const customEventName = config?.customEventName || 'rw:outcome';
  const postMessageType = config?.postMessageType || 'rw:outcome';
  const storageKey = config?.storageKey || 'rw_outcome';
  const consumeStorageKey = config?.consumeStorageKey !== false;
  const allowedMessageOrigins = config?.allowedMessageOrigins;

  const lastStorageValueRef = useRef<string | null>(null);

  const sessionId = useMemo(() => getSessionId(sessionConfig), [sessionConfig]);

  const emit = useCallback(
    (raw: unknown, source: 'api' | 'storage' | 'message' | 'event') => {
      if (!emitEnabled) return;
      const parsed = parseMaybeJson(raw);
      if (Array.isArray(parsed)) {
        parsed.forEach(item => emit(item, source));
        return;
      }
      if (isPlainObject(parsed) && Array.isArray((parsed as Record<string, unknown>).outcomes)) {
        ((parsed as Record<string, unknown>).outcomes as unknown[]).forEach(item =>
          emit(item, source)
        );
        return;
      }
      const outcome = normalizeOutcome(parsed);
      if (!outcome) return;

      const outcomeThreadIdRaw = (outcome as Record<string, unknown>).threadId;
      const outcomeThreadId = typeof outcomeThreadIdRaw === 'string' ? outcomeThreadIdRaw : null;
      const effectiveThreadId = threadId || outcomeThreadId;

      const outcomeSessionIdRaw = (outcome as Record<string, unknown>).sessionId;
      const outcomeSessionId = typeof outcomeSessionIdRaw === 'string' ? outcomeSessionIdRaw : null;
      const effectiveSessionId = sessionId || outcomeSessionId;

      const provider = outcome.provider || config?.provider;
      const payload: OutcomePayload & { threadId?: string; sessionId?: string | null } = {
        ...outcome,
        ...(provider ? { provider } : null),
        ...(effectiveThreadId ? { threadId: effectiveThreadId } : null),
        ...(effectiveSessionId ? { sessionId: effectiveSessionId } : null),
      };

      onOutcome?.(payload);
      trackAnalytics(action, {
        provider: provider || undefined,
        outcome: outcome.name,
        outcomeCategory: outcome.category || undefined,
        outcomeStatus: outcome.status || undefined,
        outcomeId: outcome.id || undefined,
        resourceType: outcome.resourceType || undefined,
        resourceId: outcome.resourceId || undefined,
        quantity: outcome.quantity,
        amount: outcome.amount,
        currency: outcome.currency,
        outcomeData: outcome.data,
        source,
        threadId: effectiveThreadId || undefined,
        widgetSessionId: effectiveSessionId || undefined,
      });
    },
    [action, config?.provider, emitEnabled, onOutcome, sessionId, threadId, trackAnalytics]
  );

  const trackOutcome = useCallback(
    (raw: unknown) => {
      emit(raw, 'api');
    },
    [emit]
  );

  useEffect(() => {
    if (!listenEnabled) return undefined;
    if (typeof window === 'undefined') return undefined;

    // Best-effort: process any existing outcome stored before we mounted.
    try {
      const existing = window.localStorage?.getItem?.(storageKey) || null;
      if (existing && existing !== lastStorageValueRef.current) {
        lastStorageValueRef.current = existing;
        emit(existing, 'storage');
        if (consumeStorageKey) {
          window.localStorage?.removeItem?.(storageKey);
        }
      }
    } catch {
      // localStorage may be unavailable
    }

    const onStorage = (event: StorageEvent) => {
      if (!event) return;
      if (event.key !== storageKey) return;
      if (!event.newValue) return;
      if (event.newValue === lastStorageValueRef.current) return;
      lastStorageValueRef.current = event.newValue;
      emit(event.newValue, 'storage');
      if (consumeStorageKey) {
        try {
          window.localStorage?.removeItem?.(storageKey);
        } catch {
          // ignore
        }
      }
    };

    const onMessage = (event: MessageEvent) => {
      if (!event || !event.data) return;
      if (!originAllowed(event.origin, allowedMessageOrigins)) return;
      const parsedData = parseMaybeJson(event.data);
      if (typeof parsedData === 'string') {
        emit(parsedData, 'message');
        return;
      }
      if (Array.isArray(parsedData)) {
        emit(parsedData, 'message');
        return;
      }
      if (!isPlainObject(parsedData)) return;
      const data = parsedData as Record<string, unknown>;
      if (data.type !== postMessageType) return;
      const payload = (data.payload ?? data.outcome ?? null) as unknown;
      emit(payload ?? data, 'message');
    };

    const onEvent = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      emit(detail, 'event');
    };

    window.addEventListener('storage', onStorage);
    window.addEventListener('message', onMessage);
    window.addEventListener(customEventName, onEvent as EventListener);

    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('message', onMessage);
      window.removeEventListener(customEventName, onEvent as EventListener);
    };
  }, [
    allowedMessageOrigins,
    consumeStorageKey,
    customEventName,
    emit,
    listenEnabled,
    postMessageType,
    storageKey,
  ]);

  return { trackOutcome };
}
